import React, { useEffect, useState } from "react";
import CameraCapture from "@/components/CameraCapture";
import { Camera, Check, X, ArrowRight, Gauge, Plus, Trash2, Clock } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

/**
 * Modal for handover (ready→handed_over) and return (handed_over→returned).
 * Captures odometer (start or end) + main vehicle photo + multiple additional photos.
 * Also allows adjusting the actual handover/return time before confirming.
 */
const HandoverModal = ({ open, mode, vehicleName, defaultTime, isMaster, onClose, onConfirm }) => {
  const { t } = useLanguage();
  const [odometer, setOdometer] = useState("");
  const [photo, setPhoto] = useState("");
  const [extras, setExtras] = useState([]); // additional photos
  const [actualTime, setActualTime] = useState("");
  const [overtimeHours, setOvertimeHours] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [extraCameraOpen, setExtraCameraOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setOdometer("");
      setPhoto("");
      setExtras([]);
      setActualTime(defaultTime || "");
      setOvertimeHours("");
    }
  }, [open, defaultTime]);

  if (!open) return null;

  const isHandover = mode === "handover";
  const showOvertime = isMaster && !isHandover;
  const title = isHandover ? t("handover_title") : t("return_title");
  const odoLabel = isHandover ? t("odometer_start") : t("odometer_end");
  const cta = isHandover ? t("mark_handed_over") : t("mark_returned");

  const submit = () => {
    const km = parseInt(odometer, 10);
    if (!km || km <= 0) return;
    onConfirm({
      odometer: km,
      photoBase64: photo || "",
      additionalPhotos: extras,
      actualTime: actualTime || null,
      overtimeHours: showOvertime && overtimeHours ? parseFloat(overtimeHours) : 0,
    });
  };

  const removeExtra = (idx) => setExtras((arr) => arr.filter((_, i) => i !== idx));

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="handover-modal">
      <div className="bg-white border-4 border-[var(--rs-primary)] w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[var(--rs-border)] bg-[var(--rs-primary)] text-white">
          <div>
            <div className="rs-overline !text-white/60">{vehicleName}</div>
            <h3 className="font-[Chivo] text-2xl font-black mt-1">{title}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-white/70 hover:text-white" data-testid="handover-close-btn">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-4">
            <label className="block">
              <span className="rs-label flex items-center gap-2"><Gauge size={12} /> {odoLabel} (km) *</span>
              <input
                type="number"
                inputMode="numeric"
                min="0"
                value={odometer}
                onChange={(e) => setOdometer(e.target.value)}
                className="rs-input"
                placeholder="z.B. 45230"
                autoFocus
                data-testid="handover-odometer"
              />
              <p className="text-xs text-[var(--rs-text-muted)] mt-2">{t("odometer_helper")}</p>
            </label>

            <label className="block">
              <span className="rs-label flex items-center gap-2"><Clock size={12} /> Tatsächliche {isHandover ? "Übergabe" : "Rückgabe"}-Zeit</span>
              <input
                type="datetime-local"
                value={actualTime}
                onChange={(e) => setActualTime(e.target.value)}
                className="rs-input"
                step="60"
                data-testid="handover-actual-time"
              />
              <p className="text-xs text-[var(--rs-text-muted)] mt-2">Bei Abweichung von der ursprünglichen Zeit anpassbar.</p>
            </label>

            {showOvertime && (
              <label className="block">
                <span className="rs-label flex items-center gap-2">⏱ Überzeit (angebrochene Stunden)</span>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={overtimeHours}
                  onChange={(e) => setOvertimeHours(e.target.value)}
                  className="rs-input"
                  placeholder="0"
                  data-testid="handover-overtime-hours"
                />
                <p className="text-xs text-[var(--rs-text-muted)] mt-2">
                  Pro angebrochene Stunde + CHF 25.– (wird automatisch berechnet)
                </p>
              </label>
            )}
          </div>

          <div>
            <span className="rs-label">{t("vehicle_photo")} (Hauptbild)</span>
            <div className="aspect-[4/3] border-2 border-dashed border-[var(--rs-text)] bg-[var(--rs-surface)] flex items-center justify-center overflow-hidden">
              {photo ? (
                <img src={photo} alt="vehicle" className="w-full h-full object-cover" data-testid="handover-photo" />
              ) : (
                <div className="text-[var(--rs-text-muted)] text-sm px-4 text-center">{t("vehicle_photo_hint")}</div>
              )}
            </div>
            <button
              type="button"
              className="rs-btn-secondary mt-3 w-full !py-3 !text-xs"
              onClick={() => setCameraOpen(true)}
              data-testid="handover-camera-btn"
            >
              <Camera size={14} className="mr-2" />
              {photo ? t("btn_retake") : t("btn_capture")}
            </button>
          </div>
        </div>

        {/* Additional photos */}
        <div className="px-5 pb-5">
          <div className="flex items-center justify-between mb-3">
            <span className="rs-label">Zusätzliche Fotos (Schäden, Details)</span>
            <button
              type="button"
              className="rs-btn-ghost !text-xs"
              onClick={() => setExtraCameraOpen(true)}
              data-testid="handover-add-extra-btn"
            >
              <Plus size={14} className="mr-1" /> Foto hinzufügen
            </button>
          </div>
          {extras.length === 0 ? (
            <div className="text-xs text-[var(--rs-text-muted)] border border-dashed border-[var(--rs-border)] py-4 px-3 text-center">
              Noch keine zusätzlichen Fotos. Über „Foto hinzufügen" beliebig viele Bilder erfassen.
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {extras.map((src, i) => (
                <div key={i} className="relative aspect-square bg-[var(--rs-surface)] border border-[var(--rs-border)] overflow-hidden" data-testid={`handover-extra-${i}`}>
                  <img src={src} alt={`extra-${i}`} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeExtra(i)}
                    className="absolute top-1 right-1 bg-red-600 text-white p-1 hover:bg-red-700"
                    data-testid={`handover-extra-remove-${i}`}
                    title="Entfernen"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-5 border-t border-[var(--rs-border)] flex justify-between gap-3">
          <button type="button" className="rs-btn-secondary !py-3 !text-xs" onClick={onClose} data-testid="handover-cancel-btn">
            {t("btn_back")}
          </button>
          <button
            type="button"
            className="rs-btn-primary !py-3 !text-xs disabled:opacity-50"
            onClick={submit}
            disabled={!odometer || parseInt(odometer, 10) <= 0}
            data-testid="handover-confirm-btn"
          >
            <Check size={14} className="mr-2" />
            {cta}
            <ArrowRight size={14} className="ml-2" />
          </button>
        </div>
      </div>

      {cameraOpen && (
        <CameraCapture
          initialImage={photo}
          onCancel={() => setCameraOpen(false)}
          onCapture={(d) => {
            setPhoto(d);
            setCameraOpen(false);
          }}
        />
      )}
      {extraCameraOpen && (
        <CameraCapture
          onCancel={() => setExtraCameraOpen(false)}
          onCapture={(d) => {
            setExtras((arr) => [...arr, d]);
            setExtraCameraOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default HandoverModal;
